import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowUpRight, Zap, Shield } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import SectionLabel from "@/components/site/SectionLabel";
import { API } from "@/lib/site";

const fieldClass =
    "w-full bg-[#020617] border border-[#1a2a4a] focus:border-[#1E90FF] focus:ring-1 focus:ring-[#1E90FF] outline-none text-white placeholder-white/30 px-4 py-3 text-sm transition-colors";

const COMMITMENTS = [
    {
        key: "commit_show_up",
        label: "I will show up to every coaching session, on time.",
    },
    {
        key: "commit_field_work",
        label: "I will do my weekly field work — no excuses.",
    },
    {
        key: "commit_honesty",
        label: "I will be brutally honest with my coach about my struggles.",
    },
    {
        key: "commit_no_quit",
        label: "I will not quit when it gets uncomfortable.",
    },
    {
        key: "commit_ready_to_start",
        label: "I'm mentally and financially ready to start within 7 days.",
    },
];

const EMPTY = {
    name: "",
    email: "",
    phone: "",
    consultation_id: null,
    why_now: "",
    biggest_fear: "",
    success_vision: "",
    past_attempts: "",
    past_attempts_why_failed: "",
    time_per_week: "",
    weekly_schedule: "",
    financial_ready: false,
    financial_explain: "",
    coachability_score: 7,
    coachability_explain: "",
    current_lifestyle: "",
    dating_status: "",
    commit_show_up: false,
    commit_field_work: false,
    commit_honesty: false,
    commit_no_quit: false,
    commit_ready_to_start: false,
    anything_else: "",
};

export default function Apply() {
    const { token } = useParams();
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(Boolean(token));
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        document.title = "Application | 90 Day Alpha";
    }, []);

    useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                const { data } = await axios.get(
                    `${API}/applications/by-token/${token}`
                );
                setForm((f) => ({
                    ...f,
                    name: data.name || "",
                    email: data.email || "",
                    phone: data.phone || "",
                    consultation_id: data.consultation_id || null,
                }));
                if (data.status && data.status !== "pending") {
                    setSubmitted(true);
                }
            } catch {
                toast.error("This application link is invalid or expired.");
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const update = (key) => (e) => {
        const val =
            e?.target?.type === "checkbox"
                ? e.target.checked
                : e?.target?.value ?? e;
        setForm((f) => ({ ...f, [key]: val }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const required = [
            "name",
            "email",
            "phone",
            "why_now",
            "biggest_fear",
            "success_vision",
            "past_attempts",
            "past_attempts_why_failed",
            "time_per_week",
            "weekly_schedule",
            "current_lifestyle",
            "dating_status",
        ];
        for (const k of required) {
            if (!String(form[k] || "").trim()) {
                toast.error("Every section is required to submit.");
                return;
            }
        }
        const allCommits = COMMITMENTS.every((c) => form[c.key]);
        if (!allCommits) {
            toast.error("You must check every commitment to apply.");
            return;
        }
        try {
            setSubmitting(true);
            await axios.post(`${API}/applications`, form);
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Submission failed. Try again in a moment."
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[100vh] electric-bg flex items-center justify-center">
                <Loader2 className="animate-spin text-[#00B7FF]" size={20} />
            </div>
        );
    }

    if (submitted) {
        return (
            <div
                data-testid="apply-success-page"
                className="min-h-[100vh] electric-bg flex items-center justify-center px-6 py-32 relative overflow-hidden"
            >
                <div
                    aria-hidden
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#1E90FF]/15 blur-[160px] animate-pulse-glow pointer-events-none"
                />
                <div className="relative max-w-2xl text-center">
                    <Reveal>
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#1E90FF]/10 border border-[#1E90FF]/40 mb-2 glow-blue">
                            <CheckCircle2
                                size={40}
                                className="text-[#00B7FF]"
                                strokeWidth={1.5}
                            />
                        </div>
                        <p className="mt-8 text-xs uppercase tracking-[0.3em] font-semibold text-[#00B7FF]">
                            Application Received
                        </p>
                        <h1 className="mt-6 font-display font-black text-white text-4xl md:text-6xl tracking-tighter leading-[1.02]">
                            You did the work.{" "}
                            <span className="text-[#D4AF37]">Now we review.</span>
                        </h1>
                        <p className="mt-8 text-white/75 leading-relaxed text-base sm:text-lg">
                            Your coach will personally review your application
                            within 48 hours. If you're a fit, you'll get an
                            email with your onboarding link, payment options,
                            and start date. If not, we'll tell you straight.
                        </p>
                        <div className="mt-12 flex flex-col sm:flex-row gap-4 items-center justify-center">
                            <Link
                                to="/"
                                data-testid="apply-success-home"
                                className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-[0.18em] text-xs hover:bg-[#C5A059] transition-all"
                            >
                                Back To Home <ArrowUpRight size={16} />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="apply-page" className="electric-bg relative overflow-hidden">
            <div
                aria-hidden
                className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#1E90FF]/10 blur-[160px] pointer-events-none"
            />

            <section className="relative pt-32 pb-8 md:pt-40 md:pb-10">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <SectionLabel testId="apply-label">
                            Program Application
                        </SectionLabel>
                        <h1 className="mt-6 font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.98]">
                            Show us you're{" "}
                            <span className="text-[#00B7FF] text-glow-blue">
                                serious
                            </span>
                            .
                        </h1>
                        <p className="mt-6 text-white/75 leading-relaxed text-base max-w-2xl">
                            This application is the gate between the
                            consultation call and the program itself. Be honest.
                            Be specific. Vague answers get rejected — we only
                            work with men who are ready to actually do this.
                        </p>
                        <div className="mt-6 inline-flex items-center gap-2 text-[#a3b5d1] text-[11px] uppercase tracking-[0.2em] font-semibold">
                            <Zap size={12} className="text-[#00B7FF]" fill="currentColor" />
                            Estimated time · 10–15 focused minutes
                        </div>
                    </Reveal>
                </div>
            </section>

            <form
                data-testid="apply-form"
                onSubmit={handleSubmit}
                className="relative pb-32"
            >
                <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-6">
                    <Block label="01 · Contact">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <FInput
                                testId="apply-name"
                                placeholder="Full Name *"
                                value={form.name}
                                onChange={update("name")}
                                disabled={Boolean(token && form.name)}
                            />
                            <FInput
                                testId="apply-email"
                                type="email"
                                placeholder="Email *"
                                value={form.email}
                                onChange={update("email")}
                                disabled={Boolean(token && form.email)}
                            />
                            <FInput
                                testId="apply-phone"
                                type="tel"
                                placeholder="Phone *"
                                value={form.phone}
                                onChange={update("phone")}
                                disabled={Boolean(token && form.phone)}
                            />
                        </div>
                    </Block>

                    <Block
                        label="02 · Motivation"
                        helper="Why now, what you're afraid of, what success looks like."
                    >
                        <FTextarea
                            testId="apply-why_now"
                            placeholder="Why are you ready to commit NOW? What's the trigger that brought you here today? *"
                            value={form.why_now}
                            onChange={update("why_now")}
                            rows={4}
                        />
                        <FTextarea
                            testId="apply-biggest_fear"
                            placeholder="What's your biggest fear about going through this program? *"
                            value={form.biggest_fear}
                            onChange={update("biggest_fear")}
                            rows={3}
                        />
                        <FTextarea
                            testId="apply-success_vision"
                            placeholder="Describe what your life looks like 90 days from now if this works. Be specific. *"
                            value={form.success_vision}
                            onChange={update("success_vision")}
                            rows={4}
                        />
                    </Block>

                    <Block
                        label="03 · Past Attempts"
                        helper="What have you tried, and why hasn't it worked yet?"
                    >
                        <FTextarea
                            testId="apply-past_attempts"
                            placeholder="What have you tried before to fix this — books, programs, dating apps, therapy, anything? *"
                            value={form.past_attempts}
                            onChange={update("past_attempts")}
                            rows={4}
                        />
                        <FTextarea
                            testId="apply-past_attempts_why_failed"
                            placeholder="Why did those past attempts fall short? Be honest. *"
                            value={form.past_attempts_why_failed}
                            onChange={update("past_attempts_why_failed")}
                            rows={3}
                        />
                    </Block>

                    <Block
                        label="04 · Time, Money, Mindset"
                        helper="The three readiness checks."
                    >
                        <FInput
                            testId="apply-time_per_week"
                            placeholder="How many focused hours per week can you commit (5+ minimum)? *"
                            value={form.time_per_week}
                            onChange={update("time_per_week")}
                        />
                        <FTextarea
                            testId="apply-weekly_schedule"
                            placeholder="Walk us through your typical week (work, family, training, social). Where will the coaching hours come from? *"
                            value={form.weekly_schedule}
                            onChange={update("weekly_schedule")}
                            rows={4}
                        />
                        <Checkbox
                            testId="apply-financial_ready"
                            checked={form.financial_ready}
                            onChange={update("financial_ready")}
                            label="I'm financially ready to invest $2,999 (or $1,049 × 3) within the next 7 days."
                        />
                        <FTextarea
                            testId="apply-financial_explain"
                            placeholder="Add context on the financial commitment (optional, but appreciated)."
                            value={form.financial_explain}
                            onChange={update("financial_explain")}
                            rows={2}
                        />
                        <div className="border border-[#1a2a4a] bg-[#020617] p-4">
                            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-white/65 mb-2">
                                How coachable are you on a 1–10 scale?{" "}
                                <span className="text-[#00B7FF]">*</span>
                            </label>
                            <div className="flex items-center gap-4">
                                <input
                                    data-testid="apply-coachability_score"
                                    type="range"
                                    min={1}
                                    max={10}
                                    value={form.coachability_score}
                                    onChange={update("coachability_score")}
                                    className="flex-1 accent-[#1E90FF]"
                                />
                                <span className="font-display font-black text-[#00B7FF] text-2xl tracking-tight w-10 text-right">
                                    {form.coachability_score}
                                </span>
                            </div>
                        </div>
                        <FTextarea
                            testId="apply-coachability_explain"
                            placeholder="Why that score? Give an example of a time you took hard feedback well — or didn't."
                            value={form.coachability_explain}
                            onChange={update("coachability_explain")}
                            rows={3}
                        />
                    </Block>

                    <Block
                        label="05 · Current Lifestyle"
                        helper="Where you are today, no spin."
                    >
                        <FTextarea
                            testId="apply-current_lifestyle"
                            placeholder="Describe your current daily lifestyle — sleep, diet, training, work, social. Be honest. *"
                            value={form.current_lifestyle}
                            onChange={update("current_lifestyle")}
                            rows={4}
                        />
                        <FInput
                            testId="apply-dating_status"
                            placeholder="Current dating status (single, dating casually, just out of a relationship, etc.) *"
                            value={form.dating_status}
                            onChange={update("dating_status")}
                        />
                    </Block>

                    <Block
                        label="06 · The Standard"
                        helper="The five non-negotiables. We accept no exceptions."
                    >
                        <div className="space-y-3">
                            {COMMITMENTS.map((c) => (
                                <Checkbox
                                    key={c.key}
                                    testId={`apply-${c.key}`}
                                    checked={form[c.key]}
                                    onChange={update(c.key)}
                                    label={c.label}
                                />
                            ))}
                        </div>
                    </Block>

                    <Block label="07 · Anything else">
                        <FTextarea
                            testId="apply-anything_else"
                            placeholder="Anything we should know? (optional)"
                            value={form.anything_else}
                            onChange={update("anything_else")}
                            rows={3}
                        />
                    </Block>

                    <div className="bg-[#060c1e] border border-[#1a2a4a] p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
                        <div className="flex items-center gap-3 text-white/55 text-[11px] uppercase tracking-[0.2em] font-semibold">
                            <Shield size={14} className="text-[#00B7FF]" />
                            Honesty filter · vague answers get rejected
                        </div>
                        <button
                            type="submit"
                            data-testid="apply-submit"
                            disabled={submitting}
                            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-[0.18em] text-xs hover:bg-[#C5A059] transition-all disabled:opacity-60 shadow-[0_0_40px_rgba(212,175,55,0.25)]"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={14} className="animate-spin" />
                                    Submitting
                                </>
                            ) : (
                                <>
                                    Submit Application{" "}
                                    <ArrowUpRight size={14} />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}

function Block({ label, helper, children }) {
    return (
        <div className="bg-[#060c1e] border border-[#1a2a4a] p-6 md:p-7 space-y-4">
            <div>
                <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#00B7FF]">
                    {label}
                </p>
                {helper && (
                    <p className="mt-1 text-white/55 text-sm">{helper}</p>
                )}
            </div>
            {children}
        </div>
    );
}

function FInput({ testId, value, onChange, placeholder, type = "text", disabled }) {
    return (
        <input
            data-testid={testId}
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            disabled={disabled}
            className={`${fieldClass} disabled:opacity-50`}
        />
    );
}

function FTextarea({ testId, value, onChange, placeholder, rows = 4 }) {
    return (
        <textarea
            data-testid={testId}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            rows={rows}
            className={`${fieldClass} resize-none`}
        />
    );
}

function Checkbox({ testId, checked, onChange, label }) {
    return (
        <label className="flex items-start gap-3 cursor-pointer group">
            <input
                type="checkbox"
                data-testid={testId}
                checked={checked}
                onChange={onChange}
                className="mt-1 w-5 h-5 accent-[#1E90FF] cursor-pointer"
            />
            <span className="text-sm text-white/85 group-hover:text-white leading-relaxed">
                {label}
            </span>
        </label>
    );
}
